// src/components/reviewer/ReviewContent.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  ChevronLeft,
  FileText,
  Download,
  Eye,
  AlertCircle,
  CheckCircle,
  Calendar,
  Mail,
  User,
  Award,
  BookOpen,
  MessageSquare,
  AlertTriangle,
  Gavel
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch(status?.toLowerCase()) {
    case 'accepted':
    case 'accept':
    case 'accept as it is':
      return 'bg-green-100 text-green-800';
    case 'rejected':
    case 'reject':
      return 'bg-red-100 text-red-800';
    case 'minor revision':
    case 'accept following minor revisions':
      return 'bg-blue-100 text-blue-800';
    case 'major revision':
    case 'reconsider following major revisions':
      return 'bg-yellow-100 text-yellow-800';
    case 'submitted':
    case 'pending':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper function to get score color
const getScoreColor = (score) => {
  if (score >= 4) return 'bg-green-100 text-green-700';
  if (score >= 3) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
};

const ReviewContent = () => {
  const { reviewId, articleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isEditorRoute = location.pathname.includes('editors');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState({ pdf: false, excel: false });
  const [reviewData, setReviewData] = useState(null);
  const [manuscriptData, setManuscriptData] = useState(null);
  const [reviewerData, setReviewerData] = useState(null);

  // Fetch review data on component mount
  useEffect(() => {
    const fetchReviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.post('/editors/viewReview', {
          article_id: articleId,
          review_id: reviewId
        });

        if (response.data.success) {
          setReviewData(response.data.review);
          
          // Fetch manuscript data
          await fetchManuscriptData(articleId);
          
          // Fetch reviewer profile data
          await fetchReviewerProfile(response.data.review.reviewer_email);
        } else {
          setError(response.data.message || 'Failed to load review');
        }
      } catch (err) {
        console.error('Error fetching review:', err);
        setError(err.response?.data?.message || 'Failed to load review data');
      } finally {
        setLoading(false);
      }
    };

    if (articleId && reviewId) {
      fetchReviewData();
    }
  }, [articleId, reviewId]);

  // Fetch manuscript data
  const fetchManuscriptData = async (articleId) => {
    try {
      const response = await axios.post('/editors/getSubmissionData', { id: articleId });
      if (response.data.success) {
        setManuscriptData(response.data.articles);
      } else {
        // Set fallback data
        setManuscriptData({
          id: articleId,
          title: 'Manuscript Title',
          authors: [{ name: 'Author Name', email: 'author@example.com' }],
          submittedDate: new Date().toISOString(),
          type: 'Research Article',
          discipline: 'Not specified',
          isWomenInScience: false,
          isBelispointAcademic: false,
          isKidnappingForRansom: false
        });
      }
    } catch (err) {
      console.error('Error fetching manuscript:', err);
      // Set fallback data
      setManuscriptData({
        id: articleId,
        title: 'Manuscript Title',
        authors: [{ name: 'Author Name', email: 'author@example.com' }],
        submittedDate: new Date().toISOString(),
        type: 'Research Article',
        discipline: 'Not specified',
        isWomenInScience: false,
        isBelispointAcademic: false,
        isKidnappingForRansom: false
      });
    }
  };

  // Fetch reviewer profile
  const fetchReviewerProfile = async (email) => {
    try {
      // You'll need to create this endpoint
      const response = await axios.get(`/editors/reviewer-profile/${email}`);
      if (response.data.success) {
        setReviewerData(response.data.profile);
      } else {
        // Set fallback data
        setReviewerData({
          name: email?.split('@')[0] || 'Reviewer',
          email: email,
          affiliation: 'Not specified'
        });
      }
    } catch (err) {
      console.error('Error fetching reviewer profile:', err);
      // Set fallback data
      setReviewerData({
        name: email?.split('@')[0] || 'Reviewer',
        email: email,
        affiliation: 'Not specified'
      });
    }
  };

  const handleExportPDF = async () => {
    setExportLoading(prev => ({ ...prev, pdf: true }));
    try {
      const response = await axios.post('/editors/export-review-pdf', {
        articleId,
        reviewId
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `review_${articleId}_${reviewId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showToast('Failed to export PDF. Please try again.', 'error');
    } finally {
      setExportLoading(prev => ({ ...prev, pdf: false }));
    }
  };

  const handleExportExcel = async () => {
    setExportLoading(prev => ({ ...prev, excel: true }));
    try {
      const response = await axios.post('/editors/export-review-excel', {
        articleId,
        reviewId
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `review_${articleId}_${reviewId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showToast('Failed to export Excel. Please try again.', 'error');
    } finally {
      setExportLoading(prev => ({ ...prev, excel: false }));
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading review content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Review</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!reviewData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No review data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header with Navigation and Export Buttons */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={handleGoBack}
              className="flex items-center text-gray-600 hover:text-purple-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Review Report</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPDF}
              disabled={exportLoading.pdf}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exportLoading.pdf ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportLoading.excel}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <FileText className="w-4 h-4" />
              {exportLoading.excel ? 'Generating...' : 'Download Excel'}
            </button>
          </div>
        </div>
      </div>

      {/* Manuscript Information Bar */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-600">Manuscript ID: {manuscriptData?.revision_id || articleId}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{manuscriptData?.title || 'Manuscript Title'}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Authors</h3>
                <div className="space-y-2">
                  {manuscriptData?.authors?.map((author, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <User className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{author.name}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                          {author.email && (
                            <span className="flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {author.email}
                            </span>
                          )}
                          {author.orcid && (
                            <span className="flex items-center">
                              <Award className="w-3 h-3 mr-1" />
                              <a href={author.orcid} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                                ORCID
                              </a>
                            </span>
                          )}
                          {author.affiliation && (
                            <span>{author.affiliation}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Submitted: {formatDate(manuscriptData?.date_submitted || manuscriptData?.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Type: {manuscriptData?.article_type || 'Not specified'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Discipline: {manuscriptData?.discipline || 'Not specified'}</span>
                </div>
                {manuscriptData?.is_women_in_contemporary_science == 1 && (
                  <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-pink-100 text-pink-800">
                    Women in Contemporary Science in Africa
                  </span>
                )}
                {manuscriptData?.is_kidnapping_for_ransom == 1 && (
                  <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                    Kidnapping for Ransom
                  </span>
                )}
                {manuscriptData?.is_belispoint_academic == 1 && (
                  <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    Belispoint Academic
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Metadata */}
      <div className="bg-purple-50 rounded-lg p-4 mb-6 border border-purple-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-purple-600 font-medium">Review ID</p>
            <p className="text-sm font-semibold text-gray-900">{reviewData.review_id}</p>
          </div>
          <div>
            <p className="text-xs text-purple-600 font-medium">Reviewer</p>
            <p className="text-sm font-semibold text-gray-900">{reviewerData?.name || reviewData.reviewer_email?.split('@')[0]}</p>
            <p className="text-xs text-gray-500">{reviewerData?.email || reviewData.reviewer_email}</p>
            <p className="text-xs text-gray-500">{reviewerData?.affiliation || ''}</p>
          </div>
          <div>
            <p className="text-xs text-purple-600 font-medium">Submitted Date</p>
            <p className="text-sm font-semibold text-gray-900">{formatDate(reviewData.dates?.completed || reviewData.dates?.created)}</p>
          </div>
          <div>
            <p className="text-xs text-purple-600 font-medium">Status</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reviewData.status)}`}>
              {reviewData.status === 'review_submitted' ? 'Completed' : reviewData.status}
            </span>
          </div>
        </div>
      </div>

      {/* Recommendation Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recommendation</h3>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(reviewData.recommendation)}`}>
            {reviewData.recommendation}
          </span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 mb-1">Section 1 Score</p>
            <p className="text-3xl font-bold text-purple-600">{reviewData.scores.section1.total}</p>
            <p className="text-xs text-gray-500">out of {reviewData.scores.section1.max}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 mb-1">Section 2 Score</p>
            <p className="text-3xl font-bold text-purple-600">{reviewData.scores.section2.total}</p>
            <p className="text-xs text-gray-500">out of {reviewData.scores.section2.max}</p>
          </div>
          <div className="bg-purple-100 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 mb-1">Total Score</p>
            <p className="text-3xl font-bold text-purple-600">{reviewData.scores.overall.total}</p>
            <p className="text-xs text-gray-500">out of {reviewData.scores.overall.max}</p>
          </div>
        </div>
      </div>

      {/* One Paragraph Summary */}
      {reviewData.comments?.one_paragraph && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            One Paragraph Summary
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-line">{reviewData.comments.one_paragraph}</p>
            {reviewData.comments.one_paragraph_file && (
              <div className="mt-4">
                <a 
                  href={reviewData.comments.one_paragraph_file} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  Download attached file
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* General Comments */}
      {reviewData.comments?.general && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            General Comments
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-line">{reviewData.comments.general}</p>
            {reviewData.comments.general_file && (
              <div className="mt-4">
                <a 
                  href={reviewData.comments.general_file} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  Download attached file
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Specific Comments */}
      {reviewData.comments?.specific && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-purple-600" />
            Specific Comments
          </h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-line">{reviewData.comments.specific}</p>
            {reviewData.comments.specific_file && (
              <div className="mt-4">
                <a 
                  href={reviewData.comments.specific_file} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                >
                  <FileText className="w-4 h-4" />
                  Download attached file
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detailed Scoring Section 1 */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Section 1: Detailed Scoring
          <span className="ml-2 text-sm font-normal text-gray-500">(Questions Regarding Title, Abstract, Introduction, Methods, Results, Discussion)</span>
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Score (1-5)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr className="bg-purple-50">
                <td colSpan="2" className="px-4 py-2 text-sm font-bold text-purple-600">QUESTIONS REGARDING TITLE AND ABSTRACT</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the title accurately reflect the subject of the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.accurately_reflect_manuscript_subject)}`}>
                    {reviewData.scores.section1.accurately_reflect_manuscript_subject || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the abstract clearly summarize the content of the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.clearly_summarize_content)}`}>
                    {reviewData.scores.section1.clearly_summarize_content || 0}
                  </span>
                </td>
              </tr>

              <tr className="bg-purple-50">
                <td colSpan="2" className="px-4 py-2 text-sm font-bold text-purple-600">QUESTIONS REGARDING INTRODUCTION</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript present what is already known and unknown (gaps) on the topic in the introduction section?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.presents_what_is_known)}`}>
                    {reviewData.scores.section1.presents_what_is_known || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript give an accurate summary of key recent research on the topic in the introduction section?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.gives_accurate_summary)}`}>
                    {reviewData.scores.section1.gives_accurate_summary || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Is the purpose (aim or objective) of the paper, its originality and novelty clear as indicated in the introduction section?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.purpose_clear)}`}>
                    {reviewData.scores.section1.purpose_clear || 0}
                  </span>
                </td>
              </tr>

              <tr className="bg-purple-50">
                <td colSpan="2" className="px-4 py-2 text-sm font-bold text-purple-600">QUESTIONS REGARDING STUDY METHODS AND ETHICS</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Is the methods section of the manuscript clear and what was done clearly and accurately described?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.method_section_clear)}`}>
                    {reviewData.scores.section1.method_section_clear || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Are the study materials, methods, instruments used, and measurements made clearly described?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.study_materials_clearly_described)}`}>
                    {reviewData.scores.section1.study_materials_clearly_described || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Are the research methods valid, reliable, reproducible, and meet requirements for best practice?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.research_method_valid)}`}>
                    {reviewData.scores.section1.research_method_valid || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Are ethical standards followed in implementing the research and in writing the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.ethical_standards)}`}>
                    {reviewData.scores.section1.ethical_standards || 0}
                  </span>
                </td>
              </tr>

              <tr className="bg-purple-50">
                <td colSpan="2" className="px-4 py-2 text-sm font-bold text-purple-600">QUESTIONS REGARDING RESULTS, GRAPHICS, AND TABLES</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">What did the study find and has this been clearly described?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.study_find_clearly_described)}`}>
                    {reviewData.scores.section1.study_find_clearly_described || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Are the results of the manuscript presented in a logical and coherent manner?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.result_presented_logical)}`}>
                    {reviewData.scores.section1.result_presented_logical || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Do the graphics used (tables and figures) clearly complement the results?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.graphics_complement_result)}`}>
                    {reviewData.scores.section1.graphics_complement_result || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Have the tables, graphics, figures, images followed highest specified standards?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.table_follow_specified_standards)}`}>
                    {reviewData.scores.section1.table_follow_specified_standards || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Do the tables, graphics, figures, images add value or distract from the content of the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.tables_add_value_or_distract)}`}>
                    {reviewData.scores.section1.tables_add_value_or_distract || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Are there issues with titles, labels, statistical notation or image quality of tables, graphics, figures, images included in the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.issues_with_title)}`}>
                    {reviewData.scores.section1.issues_with_title || 0}
                  </span>
                </td>
              </tr>

              <tr className="bg-purple-50">
                <td colSpan="2" className="px-4 py-2 text-sm font-bold text-purple-600">QUESTIONS REGARDING DISCUSSION</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript present the summary of the key findings?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_present_summary_of_key_findings)}`}>
                    {reviewData.scores.section1.manuscript_present_summary_of_key_findings || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript highlight the strengths and limitations of the study?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_highlight_strength_of_study)}`}>
                    {reviewData.scores.section1.manuscript_highlight_strength_of_study || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript compare its findings to similar papers on the topic?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_compare_findings)}`}>
                    {reviewData.scores.section1.manuscript_compare_findings || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript discuss the meaning and implications of the findings?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_discuss_meaning)}`}>
                    {reviewData.scores.section1.manuscript_discuss_meaning || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript describe and discuss the overall story formed so far on the topic?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_describes_overall_story)}`}>
                    {reviewData.scores.section1.manuscript_describes_overall_story || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Do the conclusions reflect the achievement of the study aims?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.conclusions_reflect_achievement)}`}>
                    {reviewData.scores.section1.conclusions_reflect_achievement || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript discuss the gaps or inconsistencies on the topic and ways forward described?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.manuscript_describe_gaps)}`}>
                    {reviewData.scores.section1.manuscript_describe_gaps || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm text-gray-600">Is the referencing accurate, adequate and balance in relation to the topic of the manuscript?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section1.referencing_accurate)}`}>
                    {reviewData.scores.section1.referencing_accurate || 0}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot className="bg-purple-50">
              <tr>
                <td className="px-4 py-3 text-sm font-bold text-gray-700">Section 1 Total</td>
                <td className="px-4 py-3 text-center font-bold text-purple-700">{reviewData.scores.section1.total}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Section 2: Overall Rating */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Section 2: Overall Rating of the Manuscript
        </h3>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criteria</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">Score (1-5)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">NOVELTY</td>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript address an original and well-defined question? Do the findings advance current knowledge?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section2.novelty)}`}>
                    {reviewData.scores.section2.novelty || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">QUALITY</td>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript adhere to highest standard of writing and presentation?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section2.quality)}`}>
                    {reviewData.scores.section2.quality || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">SCIENTIFIC ACCURACY</td>
                <td className="px-4 py-3 text-sm text-gray-600">Was the study design correct and sound? Are methods and analyses appropriate?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section2.scientific_accuracy)}`}>
                    {reviewData.scores.section2.scientific_accuracy || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">OVERALL MERIT</td>
                <td className="px-4 py-3 text-sm text-gray-600">Does the manuscript have overall benefit to warrant publication in ASFIRJ?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section2.overall_merit)}`}>
                    {reviewData.scores.section2.overall_merit || 0}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium text-gray-700">ENGLISH LEVEL</td>
                <td className="px-4 py-3 text-sm text-gray-600">Is the English language appropriate and understandable?</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${getScoreColor(reviewData.scores.section2.english_level)}`}>
                    {reviewData.scores.section2.english_level || 0}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot className="bg-purple-50">
              <tr>
                <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-700">Section 2 Total</td>
                <td className="px-4 py-3 text-center font-bold text-purple-700">{reviewData.scores.section2.total}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Overall Recommendation Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
          Overall Recommendation
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
            <h4 className="text-sm font-medium text-purple-800 mb-3">Recommendation</h4>
            <div className="flex items-center justify-between">
              <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(reviewData.recommendation)}`}>
                {reviewData.recommendation}
              </span>
              <span className="text-2xl font-bold text-purple-700">
                {reviewData.scores.overall.total}
              </span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Score Breakdown</h4>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Section 1:</span>
                <span className="font-semibold text-purple-600">{reviewData.scores.section1.total} / {reviewData.scores.section1.max}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Section 2:</span>
                <span className="font-semibold text-purple-600">{reviewData.scores.section2.total} / {reviewData.scores.section2.max}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                <span className="font-medium text-gray-700">Total:</span>
                <span className="font-bold text-purple-700">{reviewData.scores.overall.total} / {reviewData.scores.overall.max}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confidential Comments to Editor */}
      {reviewData.comments?.confidential && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
              Confidential Comments to Editor
            </h3>
            <span className="text-xs text-gray-500">Not shared with authors</span>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <p className="text-gray-700 whitespace-pre-line">{reviewData.comments.confidential}</p>
          </div>
        </div>
      )}

      {/* Make Decision (Editors only) */}
      {isEditorRoute && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Ready to decide on this manuscript?</h3>
            <p className="text-sm text-gray-500 mt-1">Use this review to make an acceptance, rejection, or revision decision.</p>
          </div>
          <button
            onClick={() => navigate(`/editors/decision/${articleId}`)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <Gavel className="w-5 h-5" />
            Make Decision
          </button>
        </div>
      )}

      {/* Footer with Review Summary */}
      <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500">
        <p>Review submitted on {formatDate(reviewData.dates?.completed || reviewData.dates?.created)}</p>
        <p className="mt-1">Reviewer: <b>{reviewerData?.name || reviewData.reviewer_email?.split('@')[0]}</b></p>
      </div>
    </div>
  );
};

export default ReviewContent;